import { sha256 } from 'js-sha256'
import { isPlatform } from '@ionic/react'
import { FileRec, GetScreensShowResponseSectionItems } from 'mytours-read-api-typescript'
import { ImageSizePreset, ImageSizePresetMap, ScreenSize, ScreenSizeBreakpoints } from './types'

export enum BaseUrlEnum {
	BunnyImages = 'bunnyImages',
	BunnyUploads = 'bunnyUploads',
}

export interface GenerateImageOptions {
	aspectRatio?: string
	imageSizePreset?: ImageSizePreset
	width?: number
	height?: number
	crop?: boolean
	blur?: number
	focalPoint?: number[]
}

export interface GenerateVideoOptions extends GenerateImageOptions {
	pathType?: string
}

export const passZerosBeforeNumber = (charactersSize = 0, number: number | string): string => {
	let zeros = ''
	for (let i = 0; i < charactersSize; i++) {
		zeros += '0'
	}

	const index = zeros.length - String(number).length
	// create sub string of leading 0's
	const slice = zeros.slice(0, index)
	// join id with the leading 0's
	const join = slice + String(number)

	return join
}

export const generateFilePath = (
	id: string,
	base: BaseUrlEnum,
	options: { pathType?: string; region?: string } = {},
): { base: string; path: string } => {
	const region = options.region || ENV.STQRY_REGION
	let bunnyImagesUrl = `https://${ENV.BUNNY_DOMAIN}`
	let bunnyUploadsUrl = `https://${ENV.BUNNY_UPLOADS_DOMAIN}`

	// We store all images in the US for development
	if (import.meta.env.MODE !== 'development') {
		bunnyImagesUrl = bunnyImagesUrl.replace(`${ENV.STQRY_REGION}-images`, `${region}-images`)
		bunnyUploadsUrl = bunnyUploadsUrl.replace(`${ENV.STQRY_REGION}-uploads`, `${region}-uploads`)
	}

	const join = Number(id) >= 1000000000 ? passZerosBeforeNumber(15, id) : passZerosBeforeNumber(9, id)
	// include slash every 3 digits (note it also adds slash at end of the string)
	const innerPath = join.replace(/(\d{3})/g, '$1/')
	const uploadPathType = options.pathType ? `${options.pathType}/` : 'original/'
	const path = '/uploads/' + innerPath + uploadPathType

	switch (base) {
		case BaseUrlEnum.BunnyImages:
			return { base: bunnyImagesUrl, path }
		case BaseUrlEnum.BunnyUploads:
			return { base: bunnyUploadsUrl, path }
	}
}

/**
 * Generate the url to display an image. Had to define in platform functions as uses md5 library
 */
export const generateImageUrl = (image: FileRec, options: GenerateImageOptions = {}): string => {
	if (options.imageSizePreset) {
		const size = ImageSizePresetMap[options.imageSizePreset]
		options = { ...size, ...options }
	}

	// Bunny can't transform these so we need to served the converted jpg
	const filename = image.bunny_supported ? image.filename : 'image.jpg'

	const region = image.region || ENV.STQRY_REGION
	const { base, path } = generateFilePath(image.id.toString(), BaseUrlEnum.BunnyImages, {
		pathType: image.bunny_supported ? undefined : 'jpg',
		region,
	})
	const aspectRatioParam = options.aspectRatio ? `&aspect_ratio=${options.aspectRatio}` : ''

	// Bunny won't scale up
	const heightParam = options.height ? `&height=${options.height}` : ''
	const widthParam = options.width ? `&width=${options.width}` : ''
	// Crop will be applied first before being scaled by height and weight
	const cropParam =
		options.crop && options.width && options.height && image.width && image.height
			? generateCropParam(image.width, image.height, options.width, options.height, options.focalPoint)
			: ''
	const blurParam = options.blur ? `&blur=${options.blur}` : ''
	const bunnyKey = ENV.BUNNY_KEYS[region.toLowerCase()]

	// create the query params
	const query = `${aspectRatioParam}${blurParam}${cropParam}${heightParam}&quality=70${widthParam}`.replace(/^&/, '')

	// create the secret
	const endOfTime = 2147483647
	const value = bunnyKey + path + filename + endOfTime + query
	// Adapt from the official example written for Node.js https://github.com/BunnyWay/BunnyCDN.TokenAuthentication/tree/master/nodejs
	const buffer = sha256.array(value)
	const bytes = new Uint8Array(buffer)
	let binary = ''
	for (const byte of bytes) {
		binary += String.fromCharCode(byte)
	}
	const secret = btoa(binary).replace(/\n/g, '').replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')

	// put all the pieces together
	const generated = base + path + filename + '?token=' + secret + '&' + query + '&expires=' + endOfTime
	return generated
}

const generateCropParam = (
	sourceWidth: number,
	sourceHeight: number,
	targetWidth: number,
	targetHeight: number,
	focalPoint?: number[],
): string => {
	// Scale target to be contained within source
	const heightRatio = targetHeight / sourceHeight
	const widthRatio = targetWidth / sourceWidth
	const containRatio = Math.max(heightRatio, widthRatio)

	const cropHeight = Math.round(targetHeight / containRatio)
	const cropWidth = Math.round(targetWidth / containRatio)

	let focalPointX = focalPoint && typeof focalPoint[0] === 'number' && !isNaN(focalPoint[0]) ? focalPoint[0] : 0.5
	let focalPointY = focalPoint && typeof focalPoint[1] === 'number' && !isNaN(focalPoint[1]) ? focalPoint[1] : 0.5

	// Since we have scaled the image, one of the focal dimensions won't take any effect, thus, we set it to 0.5
	// The crop area will be able to slide from left to right or from top to bottom of the source image
	if (heightRatio > widthRatio) {
		focalPointY = 0.5
		const minFocalX = cropWidth / 2 / sourceWidth
		const maxFocalX = 1 - minFocalX
		focalPointX = Math.min(maxFocalX, Math.max(minFocalX, focalPointX))
	} else {
		focalPointX = 0.5
		const minFocalY = cropHeight / 2 / sourceHeight
		const maxFocalY = 1 - minFocalY
		focalPointY = Math.min(maxFocalY, Math.max(minFocalY, focalPointY))
	}

	const cropX = Math.floor(sourceWidth * focalPointX - cropWidth / 2)
	const cropY = Math.floor(sourceHeight * (1 - focalPointY) - cropHeight / 2)

	return `&crop=${cropWidth},${cropHeight},${cropX},${cropY}`
}

export const generateImageSrcSet = (image: FileRec, options: GenerateImageOptions = {}) => {
	if (options.imageSizePreset) {
		const size = ImageSizePresetMap[options.imageSizePreset]
		// Unset imageSizePreset to not pass it to generateImageUrl
		options = { ...size, ...options, imageSizePreset: undefined }
	}

	const width = options.width
	const height = options.height

	if (!width && !height) {
		return generateImageUrl(image, options)
	}

	const densities = [1, 2, 3, 4]
	const srcSet = densities.map(density => {
		const src = generateImageUrl(image, {
			...options,
			width: width && width * density,
			height: height && height * density,
		})
		return `${src} ${density}x`
	})
	return srcSet.join(',')
}

export const generateVideoUrl = (video: FileRec, options: GenerateVideoOptions = {}): string => {
	const { filename } = video
	const sliced = filename.split('.')
	const outputFormat = sliced[sliced.length - 1]
	const { pathType } = options
	const { base, path } = generateFilePath(video.id.toString(), BaseUrlEnum.BunnyUploads, {
		pathType,
		region: video.region,
	})
	const query = `?fm=${outputFormat}`
	return base + path + filename + query
}

export const isSocialLinkType = (linkType: GetScreensShowResponseSectionItems.LinkTypeEnum): boolean =>
	[
		GetScreensShowResponseSectionItems.LinkTypeEnum.Twitter,
		GetScreensShowResponseSectionItems.LinkTypeEnum.Whatsapp,
		GetScreensShowResponseSectionItems.LinkTypeEnum.Wechat,
		GetScreensShowResponseSectionItems.LinkTypeEnum.Facebook,
		GetScreensShowResponseSectionItems.LinkTypeEnum.Instagram,
		GetScreensShowResponseSectionItems.LinkTypeEnum.Pinterest,
		GetScreensShowResponseSectionItems.LinkTypeEnum.Youtube,
		GetScreensShowResponseSectionItems.LinkTypeEnum.Vimeo,
		GetScreensShowResponseSectionItems.LinkTypeEnum.Linkedin,
		GetScreensShowResponseSectionItems.LinkTypeEnum.Tiktok,
	].includes(linkType)

export const getScreenSize = (width?: number): ScreenSize => {
	width = width === undefined ? (isPlatform('ios') ? screen.width : window.innerWidth) : width
	if (width >= ScreenSizeBreakpoints.lg) {
		return ScreenSize.lg
	} else if (width >= ScreenSizeBreakpoints.md) {
		return ScreenSize.md
	} else if (width >= ScreenSizeBreakpoints.sm) {
		return ScreenSize.sm
	} else {
		return ScreenSize.xs
	}
}

export const getParentBySelector = (element: HTMLElement, query: string): HTMLElement | null =>
	element?.closest(query) || null

export const fetchAudio = (src: string): Promise<string | undefined> =>
	new Promise(resolve => {
		// TODO: Update this place to use m4a once all audio files are converted to m4a
		fetch(src + '.mp3' + '?method=xhr', { method: 'HEAD' })
			.then(res => {
				if (res.ok) {
					src += '.mp3'
				}

				resolve(src)
			})
			.catch(() => {
				resolve(undefined)
			})
	})
